import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useCurrentPageUserId } from '../../../../hooks/useCurrentPageUserId';
import { useMemo } from 'react';
import CareersList from './shared-components/CareersList';
import ExperiencesList from './shared-components/ExperiencesList';
import { useNavigate } from 'react-router-dom';
import SquareIconButton from '../../../../components/MUIComponents/SquareIconButton';
import EditIcon from '../../../../components/Icons/EditIcon';
import { IUser } from '../../../../interfaces/user.interface';

interface SportExperienceTabProps {
  sportBackgrounds?: IUser['userSportBackgrounds'];
}

const SportExperienceTab = ({ sportBackgrounds }: SportExperienceTabProps) => {
  const { isOwnPage } = useCurrentPageUserId();
  const navigate = useNavigate();
  // Use unified IUser.userSportBackgrounds (with nested careers) from /me or /:userId.
  const isLoading = sportBackgrounds === undefined;

  const sortedSportBgs = useMemo(() => {
    if (!sportBackgrounds) return [];
    return [...sportBackgrounds].sort(
      (a, b) =>
        new Date(b.startCareerDate).getTime() -
        new Date(a.startCareerDate).getTime()
    );
  }, [sportBackgrounds]);

  const flattenedCareers = useMemo(
    () => sortedSportBgs.flatMap((sportBg: any) => sportBg.careers || []),
    [sortedSportBgs]
  );

  const hasTeams = useMemo(
    () =>
      flattenedCareers.some(
        (career: any) => career.leagueName || career.teamName
      ),
    [flattenedCareers]
  );

  const handleExperienceButtonClick = () => {
    navigate('/profile#sport-background');
  };

  const handleTeamButtonClick = () => {
    navigate('/profile#sport-background');
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      rowGap={{ xs: '16px', xl: '22px' }}
    >
      {isLoading && (
        <Skeleton
          variant="rounded"
          sx={{
            minHeight: '530px',
          }}
        />
      )}

      {sortedSportBgs?.length !== 0 && (
        <>
          <Box
            sx={{
              bgcolor: 'background.white',
              p: { xs: '36px 16px', md: '24px', lg: '36px' },
            }}
            display={'flex'}
            flexDirection={'column'}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h3" textTransform="capitalize">
                experience
              </Typography>
              {isOwnPage && (
                <SquareIconButton onClick={handleExperienceButtonClick}>
                  <EditIcon />
                </SquareIconButton>
              )}
            </Stack>

            {!!sortedSportBgs?.length && !isLoading && (
              <ExperiencesList sportBackgrounds={sortedSportBgs} />
            )}
          </Box>

          <Box
            sx={{
              bgcolor: 'background.white',
              p: { xs: '36px 16px', md: '24px', lg: '36px' },
            }}
            display={'flex'}
            flexDirection={'column'}
          >
            {' '}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h3" textTransform="capitalize">
                teams
              </Typography>
              {isOwnPage && (
                <SquareIconButton onClick={handleTeamButtonClick}>
                  <EditIcon />
                </SquareIconButton>
              )}
            </Stack>
            {!!sortedSportBgs?.length &&
              !isLoading &&
              (hasTeams ? (
                <CareersList careers={flattenedCareers} />
              ) : (
                <Typography variant="body3" color="text.secondary" mt={3}>
                  No teams listed.
                </Typography>
              ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default SportExperienceTab;
